"use client";
import { useParams } from "next/navigation";
import { useTranslations } from "next-intl";
import { useMemo } from "react";

interface InfoMessageProps {
  translationFieldName: string;
  translationChunkName: string;
  type: "error" | "success" | "warning";
  withPrefix?: boolean;
}

function InfoMessage({
  translationFieldName,
  translationChunkName,
  type,
  withPrefix = false,
}: InfoMessageProps) {
  const t = useTranslations(translationChunkName);
  const navParams = useParams();

  const dynamicStyle = useMemo(() => {
    switch (type) {
      case "error":
        return {
          border: "border-danger-medium",
          text: "text-danger-medium",
        };
      case "success":
        return {
          border: "border-success-medium",
          text: "text-success-medium",
        };
      case "warning":
        return {
          border: "border-warning-medium",
          text: "text-warning-medium",
        };
      default:
        return {
          border: "border-danger-medium",
          text: "text-danger-medium",
        };
    }
  }, [type]);

  const renderMessage = () => {
    if (type !== "error") {
      return t(translationFieldName);
    }

    if (t.has(translationFieldName)) {
      return t.rich(translationFieldName, {
        link: (chunks) => (
          <a
            className="text-brand-medium underline"
            href={`https://www.scott-sports.com/${navParams.country}/${navParams.lang}/company/contact`}
            rel="noopener noreferrer"
            target="_blank"
          >
            {chunks}
          </a>
        ),
      });
    }

    if (withPrefix) {
      return t("errorMessages.apiError", { error: translationFieldName });
    }

    return translationFieldName;
  };

  return (
    <div
      className={`mx-4 mb-6 border border-solid p-4 ${dynamicStyle.border} z-50 max-w-xl sm:mx-auto`}
    >
      <p
        className={`${dynamicStyle.text} text-center text-base/6`}
        role="alert"
      >
        {renderMessage()}
      </p>
    </div>
  );
}

export { InfoMessage };
